import { useState, useEffect } from 'react';
import { CONTACT } from 'config/contact';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  const [activeWorkflow, setActiveWorkflow] = useState(0);

  const workflows = [
    {
      id: 1,
      title: "Lead Capture & CRM Sync",
      steps: ["Web Form", "Make.com", "OpenAI Qualify", "HubSpot"],
      description: "Auto-qualify leads and sync to CRM instantly."
    },
    {
      id: 2,
      title: "WhatsApp Order Automations",
      steps: ["WhatsApp Msg", "n8n AI Agent", "Shopify", "Confirm"],
      description: "Handle orders 24/7 without human agents."
    },
    {
      id: 3,
      title: "Customer Support AI",
      steps: ["Incoming Ticket", "Knowledge Base", "Draft Reply", "Slack"],
      description: "Draft support responses in seconds, not minutes."
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveWorkflow((prev) => (prev + 1) % workflows.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [workflows.length]);

  return (
    <section className="relative overflow-hidden bg-background pt-16 pb-20 lg:pt-32 lg:pb-28">
      {/* Background Decor */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full z-0 pointer-events-none">
        {/* large decorative blobs only on large screens to avoid heavy paint & overflow on small devices */}
        <div className="hidden lg:block absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px] opacity-50" />
        <div className="hidden lg:block absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[100px] opacity-50" />
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Copy & CTA */}
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              <span className="text-sm font-semibold tracking-wide uppercase">Now accepting new clients in India</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-text-primary mb-6">
              Automate Your Business <br className="hidden lg:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
              Accelerate Your Growth
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-text-secondary mb-8 leading-relaxed">
              We build intelligent <span className="font-semibold text-text-primary">n8n</span> & <span className="font-semibold text-text-primary">Make.com</span> workflows that run your business on autopilot. Save 40+ hours every month and focus on scaling your Business.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Link to="/contact" className="w-full sm:w-auto">
                <Button 
                  variant="default" 
                  size="lg" 
                  iconName="Calendar" 
                  className="w-full shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all"
                >
                  Book Free Automation Audit
                </Button>
              </Link>
              
              <a 
                href={CONTACT.whatsappLink}
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full sm:w-auto"
              >
                <Button 
                  variant="outline" 
                  size="lg" 
                  iconName="MessageCircle" 
                  className="w-full"
                >
                  Chat on WhatsApp
                </Button>
              </a>
            </div>

            {/* Social Proof */}
            <div className="flex items-center gap-4 text-sm text-text-secondary">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-10 h-10 rounded-full border-2 border-background bg-slate-100 flex items-center justify-center overflow-hidden">
                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${i}`} alt="Client" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
              <div className="flex flex-col">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <Icon key={i} name="Star" className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-current" />
                  ))}
                </div>
                <span>Trusted by growing businesses</span>
              </div>
            </div>
          </div>

          {/* Right Column: Visual Automation Simulator */}
          <div className="relative lg:pl-10">
            <div className="relative bg-card/80 backdrop-blur-sm border border-border rounded-2xl shadow-2xl overflow-hidden">
              {/* Fake Browser Header */}
              <div className="px-4 py-3 border-b border-border bg-muted/50 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400/80" />
                  <div className="w-3 h-3 rounded-full bg-green-400/80" />
                </div>
                <div className="ml-4 px-3 py-1 bg-background rounded text-[10px] text-text-secondary font-mono border border-border">
                  running: workflow_automation.exe
                </div>
              </div>

              <div className="p-6 sm:p-8 space-y-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-text-primary">Live Scenarios</h3>
                  <span className="flex items-center gap-2 text-xs text-success font-medium bg-success/10 px-2 py-1 rounded-full">
                    <span className="w-2 h-2 bg-success rounded-full animate-pulse" />
                    System Active
                  </span>
                </div>

                <div className="space-y-4">
                  {workflows.map((workflow, index) => (
                    <div
                      key={workflow.id}
                      onClick={() => setActiveWorkflow(index)}
                      className={`cursor-pointer group relative p-4 rounded-xl border transition-all duration-300 ${
                        activeWorkflow === index
                          ? 'bg-primary/5 border-primary shadow-sm'
                          : 'bg-background border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h4 className={`font-medium text-sm transition-colors ${
                          activeWorkflow === index ? 'text-primary' : 'text-text-primary'
                        }`}>
                          {workflow.title}
                        </h4>
                        {activeWorkflow === index && (
                          <Icon name="Activity" size={16} className="text-primary animate-pulse" />
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 overflow-hidden flex-wrap">
                        {workflow.steps.map((step, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-text-secondary sm:whitespace-nowrap">
                             <span className="px-2 py-1 bg-background border border-border rounded shadow-sm">
                               {step}
                             </span>
                             {i < workflow.steps.length - 1 && (
                               <Icon name="ArrowRight" className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-muted-foreground/50" />
                             )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Decorative Floating Elements */}
            <div className="absolute -top-10 -right-10 w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl opacity-20 blur-xl rotate-12 animate-pulse" />
            <div className="absolute -bottom-5 -left-5 w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full opacity-20 blur-xl delay-700 animate-pulse" />
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;